import { Input } from 'antd';
import React, { useEffect, useState } from 'react';

interface TimeInputProps {
  value?: number; // 초 단위
  onChange?: (value: number | undefined) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}

/**
 * 시간을 HH:MM:SS 형식으로 입력받고 초 단위로 변환하여 반환하는 컴포넌트
 */
const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  style,
  placeholder = '00:00:00',
}) => {
  // 초 단위를 HH:MM:SS 형식으로 변환
  const secondsToTimeString = (seconds: number | undefined): string => {
    if (!seconds || seconds <= 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // HH:MM:SS 형식을 초 단위로 변환
  const timeStringToSeconds = (timeString: string): number | undefined => {
    if (!timeString || timeString.trim() === '') return undefined;

    // HH:MM:SS 또는 HH:MM 형식 처리
    const parts = timeString.split(':').map(part => parseInt(part.trim(), 10));

    if (parts.length === 2) {
      // HH:MM 형식
      const [hours, minutes] = parts;
      if (isNaN(hours) || isNaN(minutes)) return undefined;
      return hours * 3600 + minutes * 60;
    } else if (parts.length === 3) {
      // HH:MM:SS 형식
      const [hours, minutes, seconds] = parts;
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return undefined;
      return hours * 3600 + minutes * 60 + seconds;
    }

    return undefined;
  };

  const [inputValue, setInputValue] = useState<string>(secondsToTimeString(value));
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // value가 외부에서 변경되면 inputValue 업데이트 (포커스가 없을 때만)
  useEffect(() => {
    if (!isFocused) {
      setInputValue(secondsToTimeString(value));
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // 실시간으로 초 단위로 변환하여 onChange 호출
    const seconds = timeStringToSeconds(newValue);
    if (onChange) {
      onChange(seconds);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // 포커스를 잃을 때 형식 검증 및 정규화
    const seconds = timeStringToSeconds(inputValue);
    if (seconds !== undefined) {
      setInputValue(secondsToTimeString(seconds));
    } else {
      // 유효하지 않은 값이면 기본값으로 설정
      setInputValue('00:00:00');
      if (onChange) {
        onChange(undefined);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // 숫자, 콜론, 백스페이스, 삭제, 화살표 키만 허용
    if (
      !/[0-9:]/.test(e.key) &&
      e.key !== 'Backspace' &&
      e.key !== 'Delete' &&
      e.key !== 'ArrowLeft' &&
      e.key !== 'ArrowRight' &&
      e.key !== 'Tab' &&
      e.key !== 'Enter'
    ) {
      e.preventDefault();
    }
  };

  return (
    <Input
      value={inputValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      style={style}
      maxLength={8} // HH:MM:SS 형식
    />
  );
};

export default TimeInput;
